import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { resolveImage } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, MapPin, Calendar, ShieldCheck, BadgeCheck, Building2, PhoneCall, CheckCircle, Layers, ExternalLink, Lock, Crown, Sparkles, Play } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../shared/components/PageHeader";
import AuthGate from "../components/AuthGate";
import ImageGallery from "../features/property/components/ImageGallery";
import { INTERNAL_ROLES } from "../shared/components/PropertyActionBar";
// v2.79 Phase 1 — Cross-session sync: when an admin approves / rejects / deletes
// this project from any other session, re-hydrate without manual reload.
import { useDataSync } from "../lib/dataSync";
// Phase I-B.1 — Status labels are schema-driven (NO hard-coded map).
import { useProjectStatusLabels } from "../lib/projectSchemaApi";
// Phase I-B.2C — Indian price formatting + possession normalisation helpers.
import { formatINRDisplay, normalisePossession, normaliseLegacyUnitPrice } from "../lib/projectDisplayHelpers";

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, isAdmin } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [callbackBusy, setCallbackBusy] = useState(false);
  const [callbackSent, setCallbackSent] = useState(false);
  // Phase I-B.2C — Canonical configurations + inventory summary.
  // The Detail page reads BOTH so it can show per-config media + derived
  // availability/starting-price (NEVER exact unit codes or per-unit
  // prices — backend privacy strip is verified by pytest).
  const [configs, setConfigs] = useState([]);
  const [summary, setSummary] = useState(null);
  const { statusLabel } = useProjectStatusLabels();

  const fetchAll = useCallback(() => {
    if (!id) return;
    api.get(`/projects/${id}`).then((r) => setP(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Not found"));
    api.get(`/projects/${id}/configurations`).then((r) => setConfigs(r.data?.configurations || [])).catch(() => setConfigs([]));
    api.get(`/projects/${id}/inventory-summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // v2.79 Phase 1 — Re-hydrate the open detail page on cross-session changes.
  useDataSync(["projects"], (payload) => {
    if (!id) return;
    if (payload && payload.id && payload.id !== id) return;
    if (payload && payload.action === "delete" && payload.id === id) {
      toast("This project was removed.");
      nav(-1);
      return;
    }
    fetchAll();
  });

  if (err) return <div className="p-12 text-center text-[#A1A1AA]">{err}</div>;
  if (!p) return <div className="p-12 text-center text-[#A1A1AA]">Loading…</div>;

  // v2.71.81 — Project media must match PropertyDetail behaviour: full
  // image gallery (swipe + lightbox + thumbs) AND a Walkthrough Videos
  // block below. Previously this page rendered only `p.images[0]` and
  // silently dropped every additional photo + every video.
  const images = Array.isArray(p.images) ? p.images : [];
  const videos = Array.isArray(p.videos) ? p.videos : [];

  // v2.71.67 — Project callback flow (mirrors PropertyDetail). Direct lead
  // creation; no enquiry dialog. Anon → AuthGate; missing phone → toast.
  const onRequestCallback = async () => {
    if (!user) { setShowAuthGate(true); return; }
    const phone = (user.phone || "").trim();
    if (!phone) {
      toast.error("Please add a phone number in your profile to receive callbacks.");
      return;
    }
    if (callbackBusy) return;
    setCallbackBusy(true);
    try {
      await api.post("/enquiries", {
        property_id: `project:${p.id}`,
        name: user.name || user.email,
        phone,
        message: `Callback requested for project ${p.name}`,
        kind: "callback_request",
      });
      setCallbackSent(true);
      toast.success("Callback requested — our team will call you shortly.");
    } catch {
      toast.error("Couldn't request callback. Please try again.");
    }
    setCallbackBusy(false);
  };

  const showCustomerCTA = !INTERNAL_ROLES.has(user?.role);

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="project-detail">
      {/* v2.71.51 — Integrated header replaces the floating Back pill. */}
      <div className="sticky top-0 z-30 bg-[#FAFAFA] border-b border-[#E4E4E7]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10">
          <PageHeader
            title={p.name}
            subtitle={p.location || null}
            backLabel="Projects"
            home="/projects"
            borderless
            className="bg-transparent"
          />
        </div>
      </div>
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-8">

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {/* v2.71.81 — Full image gallery (swipe, arrows, lightbox)
                using the same proven `ImageGallery` component PropertyDetail
                uses. Falls back to the building icon if there are no images. */}
            {images.length > 0 ? (
              <ImageGallery images={images} title={p.name} sold={false} />
            ) : (
              <div className="aspect-[4/3] bg-[#F4F4F5] rounded-sm overflow-hidden border border-[#E4E4E7] flex items-center justify-center text-[#A1A1AA]">
                <Building2 size={80} strokeWidth={1} />
              </div>
            )}

            {/* v2.71.81 — Walkthrough Videos block — mirrors PropertyDetail
                pattern 1:1 so behaviour stays identical (autoplay off,
                native controls, preload=metadata for fast initial paint,
                playsInline for iOS Safari, aspect-video frame, lazy mobile). */}
            {videos.length > 0 && (
              <div className="mt-2" data-testid="project-videos">
                <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#52525B] mb-3 flex items-center gap-2">
                  <Play size={12} /> Walkthrough Videos
                  <span className="text-[10px] font-normal text-[#A1A1AA] normal-case tracking-normal">({videos.length})</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {videos.map((src, i) => (
                    <video
                      key={`${src}-${i}`}
                      src={resolveImage(src)}
                      controls
                      preload="metadata"
                      playsInline
                      className="w-full aspect-video bg-black border border-[#E4E4E7] rounded-sm"
                      data-testid={`project-video-${i}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#0055FF] bg-[#EEF4FF] border border-[#C7D9FF] px-2 py-0.5 rounded-sm" data-testid="project-status-label">{statusLabel(p.status)}</span>
              <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090B] mt-2" data-testid="project-name">{p.name}</h1>
              <div className="text-[13px] text-[#52525B] flex items-center gap-1 mt-1">
                <MapPin size={12} /> {p.location || "—"}
              </div>
              {p.google_maps_link && !p.project_locked && (
                <a
                  href={p.google_maps_link}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0055FF] hover:text-[#0044CC] hover:underline"
                  data-testid="project-open-maps-btn"
                >
                  <ExternalLink size={12} /> Open in Google Maps
                </a>
              )}
              {/* v2.71.65 — Project marketplace lock for builders viewing
                  competitor projects. Replaces map link with a locked-state
                  hint per spec STEP 8. */}
              {p.project_locked && (
                <div
                  className="mt-3 relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 p-3.5"
                  data-testid="project-marketplace-lock"
                >
                  <div className="flex items-start gap-2.5">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-[#0055FF] to-[#7C3AED] text-white flex items-center justify-center">
                      <Lock size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white border border-amber-200 text-[9px] uppercase tracking-[0.16em] font-bold text-[#92400E] mb-1">
                        <Crown size={8} /> Limited Intel
                      </div>
                      <div className="text-[12.5px] font-semibold text-slate-900">Exact project coordinates hidden</div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        Competing project — exact map, unit availability, and owner contact are restricted. Inquiries route via HouseEntry admin.
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <Link
                          to="/pricing"
                          className="inline-flex items-center gap-1 btn-gradient rounded-lg px-3 py-1.5 text-[11.5px] font-bold"
                          data-testid="project-lock-upgrade-btn"
                        >
                          <Sparkles size={10} /> Upgrade to unlock
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {p.expected_possession && (
                <Spec icon={<Calendar size={13} />} label="Possession" value={normalisePossession(p.expected_possession).label} />
              )}
              {summary && (summary.starting_price_inr || summary.starting_price_inr === 0) && (
                <Spec icon={<Layers size={13} />} label="Starts from" value={formatINRDisplay(summary.starting_price_inr)} />
              )}
            </div>

            {p.description && <p className="text-[13px] text-[#52525B] leading-relaxed">{p.description}</p>}

            {p.amenities && p.amenities.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] mb-1.5">Amenities</div>
                <div className="flex flex-wrap gap-1.5">
                  {p.amenities.map((a, i) => (
                    <span key={i} className="text-[11px] font-medium px-2.5 py-1 bg-[#F4F4F5] border border-[#E4E4E7] rounded-full inline-flex items-center gap-1">
                      <CheckCircle size={10} className="text-[#15803D]" /> {a}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {isAdmin && p.builder_name && (
              <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-sm" data-testid="admin-builder-block">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#B91C1C] mb-1.5">⛔ Admin Only — Builder / Owner Contact</div>
                <div className="text-[12px] text-[#09090B]">
                  <div><span className="text-[#52525B]">Builder:</span> <b>{p.builder_name}</b></div>
                  <div><span className="text-[#52525B]">Email:</span> <b>{p.builder_email || "—"}</b></div>
                </div>
              </div>
            )}

            {!isAdmin && (
              <div className="p-3 bg-[#F0FDF4] border border-[#BBF7D0] rounded-sm flex items-start gap-2" data-testid="verified-by-house-entry">
                <BadgeCheck size={18} className="text-[#15803D] mt-0.5 shrink-0" />
                <div className="text-[12px] text-[#166534]">
                  <b>Verified by House Entry</b> — Quality-checked and contacted directly by our team.
                </div>
              </div>
            )}

            {showCustomerCTA && (
              <Button
                className="btn-brand rounded-xl h-11 w-full font-semibold"
                onClick={onRequestCallback}
                disabled={callbackBusy || callbackSent}
                data-testid="project-callback-btn"
              >
                <PhoneCall size={14} className="mr-1.5" />
                {callbackSent ? "Callback Requested ✓" : callbackBusy ? "Requesting…" : "Request Callback"}
              </Button>
            )}
          </div>
        </div>

        {/* Phase I-B.2C — Canonical configurations with per-config media + derived counts.
            Falls back to the legacy `p.units` block only if canonical adoption hasn't happened yet. */}
        {configs && configs.length > 0 ? (
          <div className="mt-10">
            <h2 className="font-heading font-semibold text-2xl tracking-tight mb-4 flex items-center gap-2">
              <Layers size={18} /> Available Configurations
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="project-configs">
              {configs.filter((c) => !c.archived_at).map((c, i) => {
                const perCfgSummary = (summary?.per_configuration || []).find((x) => x.configuration_id === c.id);
                const availableCount = perCfgSummary?.available_count;
                const startsFromInr = perCfgSummary?.starting_price_inr ?? c.canonical_price_min_inr ?? null;
                const maxInr = c.canonical_price_max_inr ?? null;
                const heroImg = (c.images && c.images[0]) || null;
                const sizeLabel = c.min_area && c.max_area && c.min_area !== c.max_area
                  ? `${c.min_area} – ${c.max_area} ${c.area_unit || "sqft"}`
                  : (c.min_area ? `${c.min_area} ${c.area_unit || "sqft"}` : "—");
                return (
                  <div key={c.id || i} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`project-config-card-${i}`}>
                    {heroImg && (
                      <div className="mb-3 aspect-[4/3] bg-[#F4F4F5] rounded-sm overflow-hidden border border-[#E4E4E7]">
                        <img src={resolveImage(heroImg)} alt={c.config_type_label || c.config_type_key} className="w-full h-full object-cover" data-testid={`project-config-${i}-image`} />
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] uppercase tracking-wider font-semibold bg-[#0055FF] text-white px-2 py-0.5 rounded-sm" data-testid={`project-config-${i}-label`}>
                        {c.config_type_label || c.config_type_key}{c.variant_label ? ` · ${c.variant_label}` : ""}
                      </span>
                      {summary?.availability_locked ? (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1" data-testid={`project-config-${i}-avail-locked`}>
                          <Lock size={9} /> Availability hidden
                        </span>
                      ) : availableCount > 0 ? (
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-[#15803D]" data-testid={`project-config-${i}-avail`}>
                          {availableCount} available
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[11px] text-[#52525B]">Size</div>
                    <div className="font-heading font-semibold text-lg text-[#09090B] font-mono-data" data-testid={`project-config-${i}-size`}>
                      {sizeLabel}
                    </div>
                    <div className="text-[11px] text-[#52525B] mt-3">
                      {startsFromInr && maxInr && maxInr !== startsFromInr ? "Price range" : "Starts from"}
                    </div>
                    <div className="font-heading font-bold text-xl text-[#0055FF] font-mono-data leading-tight" data-testid={`project-config-${i}-price`}>
                      {startsFromInr && maxInr && maxInr !== startsFromInr
                        ? `${formatINRDisplay(startsFromInr)} – ${formatINRDisplay(maxInr)}`
                        : formatINRDisplay(startsFromInr)}
                    </div>
                    {c.public_notes && (
                      <div className="text-[11px] text-[#52525B] mt-2" data-testid={`project-config-${i}-notes`}>{c.public_notes}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (p.units && p.units.length > 0 && (
          <div className="mt-10">
            <h2 className="font-heading font-semibold text-2xl tracking-tight mb-4 flex items-center gap-2">
              <Layers size={18} /> Unit Configurations
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="project-units">
              {p.units.map((u, i) => (
                <div key={i} className="bg-white border border-[#E4E4E7] rounded-sm p-5" data-testid={`project-unit-card-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-wider font-semibold bg-[#0055FF] text-white px-2 py-0.5 rounded-sm">{u.bhk}</span>
                    {u.available_count > 0 && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#15803D]">{u.available_count} available</span>
                    )}
                    {p.project_locked && u.available_count === null && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1" data-testid={`project-unit-avail-locked-${i}`}>
                        <Lock size={9} /> Availability hidden
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#52525B]">Size</div>
                  <div className="font-heading font-semibold text-lg text-[#09090B] font-mono-data">
                    {(() => {
                      const v = u.size_sqft || 0;
                      if (!v) return "—";
                      const unit = (u.size_unit || "sqft").toLowerCase();
                      const labels = { sqft: "Sq Ft", sqyd: "Sq Yd", sqm: "Sq M", acre: "Acre" };
                      const label = labels[unit] || "Sq Ft";
                      let equiv = null;
                      if (unit === "sqyd") equiv = `${Math.round(v * 9)} sq ft`;
                      else if (unit === "sqm") equiv = `${Math.round(v * 10.7639)} sq ft`;
                      else if (unit === "acre") equiv = `${Math.round(v * 43560).toLocaleString("en-IN")} sq ft`;
                      else if (unit === "sqft") equiv = `${Math.round(v / 9)} Sq Yd`;
                      return <>{v} {label} {equiv && <span className="text-[12px] text-[#52525B]">({equiv})</span>}</>;
                    })()}
                  </div>
                  <div className="text-[11px] text-[#52525B] mt-3">Price range</div>
                  <div className="font-heading font-bold text-xl text-[#0055FF] font-mono-data leading-tight">
                    {formatINRDisplay(normaliseLegacyUnitPrice(u.price_min))} – {formatINRDisplay(normaliseLegacyUnitPrice(u.price_max))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* v2.71.67 — Enquiry dialog removed; anon users see AuthGate when
          requesting a callback (then the callback flow is direct). */}
      <AuthGate
        open={showAuthGate}
        onClose={() => setShowAuthGate(false)}
        action="callback"
        returnTo={`/project/${id}`}
      />
    </div>
  );
}

const Spec = ({ icon, label, value }) => (
  <div className="p-3 bg-white border border-[#E4E4E7] rounded-sm">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-[#52525B] flex items-center gap-1">{icon} {label}</div>
    <div className="font-mono-data text-[14px] font-semibold text-[#09090B]">{value}</div>
  </div>
);
